import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

/* -------------------
   Global font
   ------------------- */
const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: 'AnybodyMedium';
    src: url('../assets/fonts/Anybody-Medium.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
`;

/* -------------------
   Breakpoints
   ------------------- */
const breakpoints = {
  mobile: '40rem',
  tablet: '64rem',
};

const TracksContainer = styled.div`
  width: 100%;
  max-width: 75rem;          /* keeps the desktop max width */
  margin: 2rem auto;
  padding: 0 1rem;           /* safe gutters on small screens */
  box-sizing: border-box;

  /* optional: center text on very small devices */
  @media (max-width: ${breakpoints.mobile}) {
    text-align: center !important;
  }
`;

/* -------------------
   Base layout
   ------------------- */
const Page = styled.article`
  max-width: 110rem;
  margin: 0 auto;
  background: #efefef;
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  color: #000;
  overflow-x: hidden;
`;

const WideImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  margin: 0 0;
`;

/* -------------------
   Spotify iframe
   ------------------- */
const SpotifyFrame = styled.iframe`
  display: block;
  border-radius: 12px;
  width: 100%;
  height: 30rem;
  max-width: 140ch;
  margin: 3rem auto;
`;

/* -------------------
   Text styles
   ------------------- */
const Lead = styled.h2`
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  font-weight: 500;
  font-size: clamp(1.5rem, 2.1vw, 2.5rem);
  line-height: 1.38;
  max-width: 80ch;
  margin: 2rem auto;
  text-align: center;
`;

const Para = styled.p`
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  font-weight: 500;
  font-size: clamp(1rem, 1.6vw, 1.875rem);
  line-height: 1.38;
  max-width: 80ch;
  margin: 1rem auto;
  text-align: center;
`;

const Sub = styled.h3`
  font-weight: 900;
  font-size: clamp(1.8rem, 4.8vw, 3.5rem);
  text-align: center;
  color: #1388F9;
  line-height: 1.05;
  margin: 1rem 0 0.5rem;
`;

/* -------------------
   Footer
   ------------------- */
   const FooterBar = styled.footer`
   background: #000;
   padding: clamp(3rem, 1vh, 3rem) 0;
   text-align: center;
 
   h1 {
     font-family: 'AnybodyBlack', 'Anybody', sans-serif;
     color: #fff;
     font-size: clamp(2.6rem, 8vw, 8rem);
     font-weight: 900;
     margin: 0;
     line-height: 1;
   }
 `;
/* ===============================================================
   Component
   =============================================================== */
export default function AuroraAlea() {
  return (
    <Page>
      <GlobalStyles />

      {/* Section 1: Intro */}
      <WideImg src="/assets/aurora-1.png" alt="Aurora device in use" />
      <Para style={{ fontWeight: 900, marginBottom: '2rem' }}>
        IN AN AGE WHERE YOU THINK YOU MIGHT HAVE HEARD AND SEEN IT ALL, AURORA AND ALEA ARE BORN. TECHNOLOGY THAT TRANSLATES MOVEMENT INTO MUSIC... STYLISHLY. GABRIEL MEALOR-PRITCHARD EXPLORES THE INTRICATE WORLD OF SOUNDSCAPES FASHIONED THROUGH MOVEMENT.
      </Para>
      <Para>
        In an imagined future where the rituals of prayer lose their grip on the human spirit, Aurora rises as a symbol of transformation. It isn't a relic of old faith, nor a mere gadget – yet a reinterpretation of how people seek connection and devotion in a world remade by technology.
      </Para>
      <Para>
        Posed as a 'tool for self-restoration,' Aurora is more than an instrument. Made in partnership with two Central Saint Martins MA Industrial Design students, Kieran Feechan and Domenico Di Paolo, Aurora is an ethereal speculative device, inviting its users to communicate with sound through movement. Feechan told us about how he became "captivated by the idea of making the invisible tangible," sparking a deep exploration into "generative, sensor-based music and how it could be used to create personal, immersive experiences." Domenico Di Paolo, one of Aurora's co-creators, explains that, "We have reflected on the power of religion to influence and shape the cultural identity of a people and a place. At the same time, spirituality has always been fluid, evolving with the passage of time and the changes in culture," leading us to beg the question, is this the next step in a new age of religion, born through sound and connection to technology?
      </Para>

      {/* Section 2: Aurora */}
      <WideImg src="/assets/aurora-2.png" alt="Aurora speculative device" />
      <Para>
        As hands sweep through the air, a series of "ultrasonic sensors" connected to a microcontroller atop an embedded PCB (printed circuit board) capture the movements and translate them into sound. "The vertical distance of the user's hand relative to the position of these sensors determines the precise note being played by the user," notes Feechan as he describes the functionality of Aurora. He explains that lifting a hand above the sensor in a vertical motion will sound as though you are playing a scale on a piano, except with Aurora, "each sensor represents a different instrument and creates entirely different sounds. The result of this is the production of a dense soundscape unique to the exact patterned motion of the user's hands, but played in a consistent scale," the creative elaborates.
        But how? You might be asking. This is all made possible by the device's connection to Ableton Live, the industry-standard music production software that embeds the motions we make toward Aurora into noise. Due to this parallax of software, the sounds simulated through Aurora can be sampled, recorded and looped into music.
      </Para>
      <Para>
        Alea, in contrast, is an extension of Aurora with a different focus. The device is less about creation and more about manipulation, "not making music from scratch," but rather "reshaping it through movement and touch," explains Feechan. A unique addition to an already niche device - made to be worn and experienced closer to the body than Aurora's structure, Alea was conceptualised by a team of five individuals: Wenda Yang, Kieran Feechan, Mandy Liu, Domenico Di Paolo, and Niki Tokunaga; and born from a continuous collaboration between like-minded creatives.
      </Para>
      <Para>
        Born as part of a "larger world-building project envisioning London in 2050," Alea pays homage to the expected fade in traditional nightclubs. "The human need for rhythmic, embodied connection remains strong. In this future, Alea offers a different kind of musical experience - one where dance, movement, and connection directly shape sound," says Feechan. In addition, Di Paolo told us that "Alea offers an experience of manipulating music through dance and bodily movement, within new spaces for musical events that are more intimate and immersive." Leading us to beg the question, what is the future going to truly look like? In a world where technology increasingly blurs the lines between sound, space and body, Alea hints at a future where music is no longer something we simply listen to, but rather something we inhabit as individuals.
      </Para>

      {/* Section 3: Alea */}
      <WideImg src="/assets/aurora-3.png" alt="Alea wearable music manipulator" />
      <Para>
        A challenge the team faced during the production process was "finding the right balance between technology and fashion," making it a priority to find an equilibrium between juggling the ability to seam both practical whilst allowing the wearer to enjoy the sensation of donning Alea as a stylised device.
      </Para>
      <Para>
        In the words of the BASE Milano's editorial team, "The days of worn-out prayer books and silent supplications are gone." Aurora was chosen to be exhibited at BASE Milano, a place dedicated to the projection of "cross-pollinating the arts with companies and technology," this spring. Di Paolo noted that, "It was truly remarkable to be in an environment filled with talented emerging designers," also highlighting that the team had created a semi-private viewing room "reminiscent of a Christian confessional" enclosed by black velvet curtains, where visitors could engage in listening sessions with Aurora. Complete with the kneeler in front of the device, inviting users to kneel as they would usually in prayer, before embarking on their sonic voyage to personal healing. "Watching people interact with something we had spent months developing and fine-tuning was both exciting and humbling. Seeing others connect with the work in such a personal way truly made it worthwhile," added Feechan. The setup acted as a makeshift barrier to allow users to fully experience the structure and its purpose away from the bustle of the ongoing expo, ensuring a closer, undisturbed connection with the process. Di Paolo went on to say, "We are now working on our next exhibition, which will take place at 'Sónar+D' in Barcelona in June," the next phase for this advanced system's promotion.
      </Para>
      <Para>
        Now that Feechan and Di Paolo, amongst the other creatives, have produced a combination of two interconnected objects, specialising in two areas of music production and manipulation, their focus moving forward is to "better understand how these [devices] can be combined in an interesting and compelling manner." The creatives spoke on their plans for the future, to which Di Paolo responded with intent on developing Alea to "take it to the next level."
      </Para>
      <Para>
        It is clear that new beginnings are upon the horizon, and their names are Aurora and Alea, the musical manipulators of tomorrow. A modern approach to spirituality through the art of sound, integrating the immersive factor of music, encouraging people to further connect to the tracks they love than they normally would. To create your very own soundscape through personal movements that reflect your specific actions and decisions is something that Aurora captures with ease. Alea allows you the possibility to change the music, and make it your own, possessing the ability to allow its user to embody the noise, and physically wear it on their sleeve. Music has truly never felt closer to home than through the sentiments of Aurora and Alea.
      </Para>
      <br />

      {/* Section 4: Closing image */}
      <WideImg src="/assets/aurora-4.png" alt="Aurora and Alea exhibit" />
      <br />
      <Para style={{ fontWeight: 700 }}>
        KIERAN FEECHAN AND DOMENICO DI PAOLO SELECT THEIR CURRENT TOP THREE FAVOURITE TRACKS AND GIVE US THE RUNDOWN ON WHY THEY WERE CHOSEN. FIND THE LINKS BELOW:
      </Para>
      <br />
      {/* Tracks by KIERAN */}
      <TracksContainer style={{ textAlign: 'right' }}>

        <Para style={{ textAlign: 'right', maxWidth: '75rem' }}>
        <span
          style={{
            color: 'black',
            fontSize: '2rem',
            fontFamily: 'Anybody',
            fontWeight: 900,
            lineHeight: '2.7rem',
            wordWrap: 'break-word',
          }}
        >
          KIERAN –<br />
          <br />
        </span>
          <span style={{ fontWeight: 900 }}>TRACK 1:</span>
          <br />
          The first track worth mentioning is Here Again, the powerful climax to the Glasgow-based band Leanover’s new EP of the same title. It’s a beautifully complex and evolving track that oscillates between pristine, haunting melody and shoegaze-inspired eeriness, creating an atmosphere that is both captivating and unplaceable. The way it builds and morphs feels truly unique, balancing delicate moments with a raw intensity that really stays with you.
          <br />
        </Para>
        <br />
        <Para style={{ textAlign: 'right', maxWidth: '75rem' }}>
          <span style={{ fontWeight: 900 }}>TRACK 2:</span>
          <br />
          I’ve recently discovered Flutter by PALOMATIC, the alias of Koji Takahashi, a pioneering figure in Japan’s early-to-mid-’90s electronic music scene. This track stands out for its seamless balance of organic and synthetic elements, crafting a dynamic, evolving soundscape that feels like it’s meant to be lived in. The track’s rich textures and ambient rhythms take you to a place of calm and reflection, yet with enough complexity to keep you engaged.
          <br />
        </Para>
        <br />
        <Para style={{ textAlign: 'right', maxWidth: '75rem' }}>
          <span style={{ fontWeight: 900 }}>TRACK 3:</span>
          <br />
          Anywhere but Here, the second track from Panda Bear’s new album, has been on constant rotation since its release. It’s a gorgeous experimental slow-jam ballad, with raw, intimate vocals paired with spoken-word passages in Portuguese, delivered by his daughter, Nadja Lennox. It’s a stripped-back, hauntingly beautiful moment on an already fantastic record – a piece that stands out for its simplicity and emotional depth.
          <br />
        </Para>
        <br />
        </TracksContainer>

      {/* Tracks by DOMENICO */}
      <TracksContainer style={{ textAlign: 'right' }}>
        <Para style={{ textAlign: 'left', maxWidth: '75rem' }}>
        <span
          style={{
            color: 'black',
            fontSize: '2rem',
            fontFamily: 'Anybody',
            fontWeight: 900,
            lineHeight: '2.7rem',
            wordWrap: 'break-word',
          }}
        >
          DOMENICO –<br />
        </span>
        <br />
          <span style={{ fontWeight: 900 }}>TRACK 1:</span>
          <br />
          “L’Ancienne” - Djrum: I have been immersing myself in his new album, Under Tangled Silence, in recent days. If I were to speak from the gut, I believe this album will have a significant influence in the near future. In particular, this track blends the grace of piano improvisation with dark, spooky percussions, which resonates deeply with my current mood.
        </Para>
        <br />
        <Para style={{ textAlign: 'left', maxWidth: '75rem' }}>
          <span style={{ fontWeight: 900 }}>TRACK 2:</span>
          <br />
          “Home” - LCD Soundsystem: I love the drum take, the 8 minutes filled with all its mistakes and imperfections. I always focus on how the cowbell never lands in the same spot, and how this keeps the listener’s attention engaged. Then, this song brings back memories of joyful times.
        </Para>
        <br />
        <Para style={{ textAlign: 'left', maxWidth: '75rem' }}>
          <span style={{ fontWeight: 900 }}>TRACK 3:</span>
          <br />
          “Pasta & Fagioli” - Celso Valli: One of my closest friends gave me this raro vinyl at the beginning of the last Design Week, so I associate it with the happiness of that moment. Additionally, “pasta e fagioli” is one of the dishes I make best in the kitchen!
        </Para>
        <br />
        </TracksContainer>

      {/* Spotify playlist */}
      <SpotifyFrame
        src="https://open.spotify.com/embed/playlist/6Vy2BZ4sywyfIhFrsx1Adz?utm_source=generator"
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        loading="lazy"
        
      />

      <FooterBar>
        <h1>RIFF MAGAZINE</h1>
      </FooterBar>
    </Page>
  );
}
